import React, { useState } from "react";
import { Alert, Button, Card, Checkbox, Form, Input, Modal, Tooltip } from "antd";
import { Link } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import '../assets/css/Dentobook.css';
import { CloseOutlined } from "@ant-design/icons";
import { isMobileOnly } from "react-device-detect";
import { tokenService } from "../services/tokenService";
import { isAnonymousPath } from "../constants/anonymousPaths";
import { getAuthenticationApi } from "../api/apiInstances";
import { useTrackEvent } from "../hooks/useTrackEvent";
import { ErrorCode } from "../utils/analytics";

const Login = () => {
    const intl = useIntl();
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [errMsg, setErrMsg] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [rememberMe, setRememberMe] = useState<boolean>(false);
    const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
    const [tempTokens, setTempTokens] = useState<{access_token: string, refresh_token: string} | null>(null);

    const [form] = Form.useForm();
    const track = useTrackEvent();

    // Track when the terms modal becomes visible. Fires once per modal open —
    // if the user rejects and logs in again, it fires again (intentional: loop detection).
    React.useEffect(() => {
        if (showTermsModal) {
            track('terms_viewed', { source: 'login_form' });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showTermsModal]);

    const onFinish = async () => {
        try {
            await form.validateFields();
            setLoading(true);
            setErrMsg('');

            // Call login endpoint using generated API client
            const authApi = getAuthenticationApi();
            const response = await authApi.login({
                email: email.trim().toLowerCase(),
                password: password
            });

            const { access_token, refresh_token, terms_accepted } = response.data;

            // Check if user needs to accept terms
            if (!terms_accepted) {
                setTempTokens({ access_token, refresh_token });
                setShowTermsModal(true);
                setLoading(false);
                return;
            }

            // Store tokens with remember me preference
            tokenService.setTokens(access_token, refresh_token, rememberMe);
            track('login_succeeded', { remember_me: rememberMe, source: 'login_form' });

            // Redirect to previous page or home
            let redirectUrl = tokenService.getAndClearRedirectUrl();

            // Don't redirect to anonymous paths like reset-password or forgot-password
            if (isAnonymousPath(redirectUrl)) {
                redirectUrl = '/';
            }

            window.location.href = redirectUrl;

        } catch (err: any) {
            const statusCode = err.response?.status;
            const errorDetail = err.response?.data?.detail;

            if ((statusCode === 403 || statusCode === 401) && errorDetail?.error_code === 'EMAIL_NOT_VERIFIED') {
                track('login_failed', { error_code: ErrorCode.EMAIL_NOT_VERIFIED, http_status: statusCode ?? 0, source: 'login_form' });
                window.location.href = `/verify-email?email=${encodeURIComponent(email.trim().toLowerCase())}`;
                return;
            } else if (statusCode === 401) {
                track('login_failed', { error_code: ErrorCode.WRONG_CREDENTIALS, http_status: 401, source: 'login_form' });
                setErrMsg('Incorrect email or password.');
            } else if (statusCode === 422 && Array.isArray(errorDetail)) {
                track('login_failed', { error_code: ErrorCode.VALIDATION_ERROR, http_status: 422, source: 'login_form' });
                // Handle 422 validation errors (Pydantic validation)
                const validationErrors = errorDetail.map((error: any) => {
                    const field = error.loc?.[error.loc.length - 1] || 'field';
                    const message = error.msg || error.ctx?.reason || 'Invalid value';
                    return `${field}: ${message}`;
                }).join('\n');
                setErrMsg(validationErrors);
            } else if (errorDetail) {
                track('login_failed', { error_code: ErrorCode.UNKNOWN, http_status: statusCode ?? 0, source: 'login_form' });
                setErrMsg(typeof errorDetail === 'string' ? errorDetail : JSON.stringify(errorDetail));
            } else {
                track('login_failed', { error_code: ErrorCode.UNKNOWN, http_status: statusCode ?? 0, source: 'login_form' });
                setErrMsg('An unexpected error occurred. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptTerms = async () => {
        if (!tempTokens) return;

        try {
            setLoading(true);

            // Call accept terms endpoint using generated API client with token
            const authApi = getAuthenticationApi();
            await authApi.acceptTerms(
                { accepted: true },
                {
                    headers: {
                        'Authorization': `Bearer ${tempTokens.access_token}`
                    }
                }
            );

            // Store tokens after accepting terms with remember me preference
            tokenService.setTokens(tempTokens.access_token, tempTokens.refresh_token, rememberMe);
            track('terms_accepted', { source: 'login_form' });
            // Signal App.tsx to fire dashboard_viewed_first_time after the redirect loads.
            // sessionStorage persists across the window.location.href navigation within the tab.
            sessionStorage.setItem('dentobook_terms_just_accepted', '1');
            track('login_succeeded', { remember_me: rememberMe, source: 'login_form' });

            // Redirect to previous page or home
            const redirectUrl = tokenService.getAndClearRedirectUrl();
            window.location.href = redirectUrl;

        } catch (err: any) {
            setErrMsg('Failed to accept terms. Please try again.');
            setShowTermsModal(false);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelTerms = () => {
        track('terms_rejected', { source: 'login_form' });
        setShowTermsModal(false);
        setTempTokens(null);
        setErrMsg('You must accept the terms and conditions to proceed.');
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
                overflow: "hidden",
            }}
        >
            <div style={{ marginBottom: "20px", textAlign: "center" }}>
                <a href="https://dentobook.com/en/" target="_blank" rel="noopener noreferrer">
                    <img
                        alt="logo"
                        className="dentobook-logo"
                        src={require("../assets/logo.png")}
                        style={{
                            width: "420px",
                            height: "104px",
                            padding: "13px 10px 13.65px 10px",
                        }}
                    />
                </a>
            </div>
            <Card
                style={{
                    width: isMobileOnly ? "90vw" : 480,
                    padding: "40px",
                    borderRadius: "30px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)"
                }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    style={{ textAlign: "left" }}
                >
                    {errMsg ? (
                        <Alert
                            message={errMsg}
                            type="error"
                            className="mb-24"
                            closeIcon={<CloseOutlined />}
                            onClose={() => setErrMsg('')}
                            showIcon
                            onClick={() => setErrMsg('')}
                        />
                    ) : null}

                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="email" />}
                        name="email"
                        rules={[
                            { required: true, message: intl.formatMessage({id: 'required_field'}) },
                            { type: "email", message: intl.formatMessage({id: 'invalid_email'}) }
                        ]}
                    >
                        <Input
                            style={{ borderRadius: "12px", height: "48px" }}
                            autoComplete="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </Form.Item>

                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="password" />}
                        name="password"
                        rules={[{ required: true, message: intl.formatMessage({id: 'required_field'}) }]}
                    >
                        <Input
                            type="password"
                            style={{ borderRadius: "12px", height: "48px" }}
                            autoComplete="current-password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                        />
                    </Form.Item>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", marginTop: "-8px" }}>
                        <Tooltip title={intl.formatMessage({id: 'remember_me_tooltip'})} placement="topLeft">
                            <Checkbox
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                style={{ fontSize: "14px" }}
                            >
                                <FormattedMessage id="remember_me" />
                            </Checkbox>
                        </Tooltip>
                        <Link to="/forgot-password" style={{ color: "#1890ff", fontSize: "14px" }}>
                            <FormattedMessage id="forgot_password" />
                        </Link>
                    </div>

                    <div className="dentobook-register-btn">
                        <Form.Item className={"dentobook-form-item dentobook-simple-input"}>
                            <Button
                                className="dentobook-btn-primary dentobook-btn-medium"
                                type="primary"
                                htmlType="submit"
                                disabled={loading}
                                style={{
                                    width: "100%",
                                    height: "48px",
                                }}
                            >
                                {loading ? <FormattedMessage id="logging_in" /> : <FormattedMessage id="login" />}
                            </Button>
                        </Form.Item>
                    </div>

                    <div style={{ textAlign: "center", marginTop: "16px" }}>
                        <span style={{ color: "#666" }}><FormattedMessage id="no_account" /> </span>
                        <Link to="/register" style={{ color: "#1890ff", fontWeight: 500 }}>
                            <FormattedMessage id="register_here" />
                        </Link>
                    </div>
                </Form>
            </Card>

            {/* Terms and Conditions Modal */}
            <Modal
                open={showTermsModal}
                closable={false}
                footer={null}
                width={isMobileOnly ? "90vw" : 600}
                centered
                maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
                styles={{body: {
                    padding: '40px 60px',
                    textAlign: 'center'
                }}}
            >
                <div>
                    <img
                        alt="logo"
                        src={require("../assets/logo.png")}
                        style={{
                            width: "240px",
                            height: "60px",
                            marginBottom: "40px"
                        }}
                    />

                    <p style={{
                        fontSize: '18px',
                        lineHeight: '1.6',
                        marginBottom: '40px',
                        color: '#333'
                    }}>
                        I have read and agreed with the{' '}
                        <a
                            href="https://dentobook.com/en/terms-and-conditions/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: '#5468ff',
                                textDecoration: 'underline'
                            }}
                        >
                            Terms and Conditions
                        </a>{' '}
                        and{' '}
                        <a
                            href="https://dentobook.com/en/gdpr/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: '#5468ff',
                                textDecoration: 'underline'
                            }}
                        >
                            General Data Protection Regulation
                        </a>
                        .
                    </p>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '16px',
                        marginTop: '32px'
                    }}>
                        <Button
                            onClick={handleCancelTerms}
                            style={{
                                borderRadius: "24px",
                                height: "48px",
                                minWidth: "140px",
                                fontSize: '16px',
                                border: '1px solid #d9d9d9',
                                color: '#5468ff'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleAcceptTerms}
                            disabled={loading}
                            style={{
                                borderRadius: "24px",
                                height: "48px",
                                minWidth: "140px",
                                fontSize: '16px',
                                backgroundColor: '#5468ff',
                                borderColor: '#5468ff'
                            }}
                        >
                            {loading ? 'Processing...' : 'Confirm'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Login;
